import { supabase } from '@/lib/supabase';
import type { User, LoginCredentials, SignupCredentials } from '@shared/types';

/**
 * ⚠️ CRITICAL WARNING: Database Query Deadlock
 *
 * Supabase database queries can HANG INDEFINITELY (deadlock, not just slow).
 * This has happened with:
 * - supabase.auth.getSession()
 * - supabase.from('users').select()
 *
 * DO NOT use Promise.race() with timeouts to "fix" slow queries.
 * Timeouts will DESTROY VALID SESSIONS and cause logout-on-refresh.
 *
 * CORRECT PATTERN:
 * - Use non-blocking patterns (.then() instead of await)
 * - Call callbacks immediately with session data
 * - Fetch additional data in background
 * - Never block authentication on database calls
 *
 * TESTING REQUIREMENTS:
 * Before committing ANY auth changes, test:
 * 1. Login on regular browser → refresh → verify still logged in
 * 2. Login on incognito → refresh → verify still logged in
 * 3. Test on both localhost AND production
 * 4. Clear cache and retest
 *
 * See CHANGELOG.md "Critical Corrections" section for history.
 */
class AuthService {
  async login(credentials: LoginCredentials): Promise<User> {
    console.log('login: starting...');

    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });

    console.log('login: signInWithPassword result', { data: data?.user?.id, error });

    if (error) {
      throw new Error(error.message);
    }

    if (!data.user) {
      throw new Error('Login failed');
    }

    // Fetch user profile data
    console.log('login: fetching profile...');
    const { data: userProfile, error: profileError } = await supabase
      .from('users')
      .select('*')
      .eq('id', data.user.id)
      .single();

    console.log('login: profile result', { userProfile: userProfile?.id, profileError });

    if (profileError || !userProfile) {
      throw new Error('Failed to fetch user profile');
    }

    console.log('login: success, returning user');
    // Cache user ID and super admin status for next page load (prevents sidebar flickering)
    localStorage.setItem('cachedUserId', userProfile.id);
    localStorage.setItem('isSuperAdmin', String(userProfile.is_super_admin || false));
    return {
      id: userProfile.id,
      email: data.user.email || '',
      fullName: userProfile.full_name,
      avatarUrl: userProfile.avatar_url,
      role: userProfile.role,
      isSuperAdmin: userProfile.is_super_admin || false,
      settings: userProfile.settings || {},
      createdAt: userProfile.created_at,
      updatedAt: userProfile.updated_at,
    };
  }

  async signup(credentials: SignupCredentials): Promise<User | { emailConfirmationRequired: true; email: string }> {
    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        data: {
          full_name: credentials.fullName,
          role: 'sales_rep',
          plan: credentials.plan || 'free',
        },
      },
    });

    if (error) {
      throw new Error(error.message);
    }

    if (!data.user) {
      throw new Error('Signup failed');
    }

    // Check if email confirmation is required (no session means email not confirmed)
    if (!data.session) {
      console.log('[SIGNUP] Email confirmation required');
      return { emailConfirmationRequired: true, email: credentials.email };
    }

    // Email confirmed or confirmation disabled - fetch user profile
    // Retry fetching user profile with exponential backoff
    const maxRetries = 5;
    let userProfile = null;
    let lastError = null;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      const delay = 500 * Math.pow(2, attempt);
      console.log(`[SIGNUP] Attempt ${attempt + 1}/${maxRetries}: waiting ${delay}ms for profile...`);
      await new Promise((resolve) => setTimeout(resolve, delay));

      const { data: profile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', data.user.id)
        .single();

      if (!profileError && profile) {
        userProfile = profile;
        console.log(`[SIGNUP] Profile found on attempt ${attempt + 1}`);
        break;
      }

      lastError = profileError;
      console.warn(`[SIGNUP] Attempt ${attempt + 1} failed:`, profileError?.message);
    }

    if (!userProfile) {
      console.error('[SIGNUP] Failed to fetch user profile after all retries:', lastError);
      throw new Error(
        'Failed to create user profile. The database trigger may not be configured. ' +
        'Please contact support or check the migration status.'
      );
    }

    return {
      id: userProfile.id,
      email: data.user.email || '',
      fullName: userProfile.full_name,
      avatarUrl: userProfile.avatar_url,
      role: userProfile.role,
      isSuperAdmin: userProfile.is_super_admin || false,
      settings: userProfile.settings || {},
      createdAt: userProfile.created_at,
      updatedAt: userProfile.updated_at,
    };
  }

  async logout(): Promise<void> {
    // Clear cached user data to prevent stale data when switching accounts
    localStorage.removeItem('cachedUserId');
    localStorage.removeItem('isSuperAdmin');
    localStorage.removeItem('cachedCompany');
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw new Error(error.message);
    }
  }

  async getCurrentUser(): Promise<User | null> {
    const startTime = Date.now();
    try {
      console.log('[AUTH] getCurrentUser: starting...', { timestamp: new Date().toISOString() });

      // Get session - let Supabase handle its own timeout (60s default)
      console.log('[AUTH] getCurrentUser: calling supabase.auth.getSession()');
      const sessionStartTime = Date.now();
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      const sessionDuration = Date.now() - sessionStartTime;
      console.log('[AUTH] getCurrentUser: getSession() completed', { durationMs: sessionDuration });

      if (sessionError) {
        console.warn('[AUTH] getCurrentUser: session error', sessionError);
        return null;
      }

      console.log('[AUTH] getCurrentUser: session check complete', {
        hasSession: !!session,
        userId: session?.user?.id,
        durationMs: Date.now() - startTime
      });

      if (!session?.user) {
        console.log('[AUTH] getCurrentUser: no session, returning null');
        return null;
      }

      // Fetch user profile from database to get full user data including settings
      console.log('[AUTH] getCurrentUser: fetching profile from database...');

      // Fetch profile - let Supabase handle its own timeout
      const profileStartTime = Date.now();
      const { data: userProfile, error: profileError } = await supabase
        .from('users')
        .select('*')
        .eq('id', session.user.id)
        .single();
      const profileDuration = Date.now() - profileStartTime;
      console.log('[AUTH] getCurrentUser: profile fetch completed', { durationMs: profileDuration });

      if (profileError) {
        console.warn('[AUTH] getCurrentUser: profile fetch error, using fallback', profileError);
        // Fallback to session data if profile fetch fails
        const fallbackUser = {
          id: session.user.id,
          email: session.user.email || '',
          fullName: session.user.user_metadata?.full_name || '',
          avatarUrl: session.user.user_metadata?.avatar_url || null,
          role: session.user.user_metadata?.role || 'sales_rep',
          isSuperAdmin: false, // Cannot be super admin if profile fetch fails
          settings: {},
          createdAt: session.user.created_at || new Date().toISOString(),
          updatedAt: session.user.updated_at || new Date().toISOString(),
        };
        console.log('[AUTH] getCurrentUser: returning fallback user', {
          userId: fallbackUser.id,
          totalDurationMs: Date.now() - startTime
        });
        return fallbackUser;
      }

      if (!userProfile) {
        console.warn('[AUTH] getCurrentUser: profile not found, returning null');
        return null;
      }

      // Cache user ID and super admin status for next page load (prevents sidebar flickering)
      localStorage.setItem('cachedUserId', userProfile.id);
      localStorage.setItem('isSuperAdmin', String(userProfile.is_super_admin || false));
      const finalUser = {
        id: userProfile.id,
        email: session.user.email || '',
        fullName: userProfile.full_name,
        avatarUrl: userProfile.avatar_url,
        role: userProfile.role,
        isSuperAdmin: userProfile.is_super_admin || false,
        settings: userProfile.settings || {},
        createdAt: userProfile.created_at,
        updatedAt: userProfile.updated_at,
      };
      console.log('[AUTH] getCurrentUser: SUCCESS - returning user with full profile', {
        userId: finalUser.id,
        hasSettings: Object.keys(finalUser.settings).length > 0,
        totalDurationMs: Date.now() - startTime
      });
      return finalUser;
    } catch (error) {
      console.error('[AUTH] getCurrentUser: unexpected error, returning null', error);
      return null;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    return !!session;
  }

  async resetPassword(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  async updatePassword(newPassword: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  async sendMagicLink(email: string): Promise<void> {
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  async signInWithGoogle(): Promise<void> {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/dashboard`,
      },
    });

    if (error) {
      throw new Error(error.message);
    }
  }

  // Listen to auth state changes
  onAuthStateChange(callback: (user: User | null) => void) {
    return supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('onAuthStateChange:', event, { hasSession: !!session });

      // Sync auth session to a shared cookie on .sellercockpit.com
      // so the marketing site signature generator can detect logged-in users
      this.syncSessionCookie(event, session);

      // On SIGNED_OUT event, explicitly clear user
      if (event === 'SIGNED_OUT') {
        callback(null);
        return;
      }

      // For all auth events with a valid session, immediately call callback with session data
      if (session?.user) {
        console.log('[AUTH] onAuthStateChange: calling callback with session data');
        // Immediately set user from session data (fast, no database call)
        // Use cached isSuperAdmin to prevent sidebar flickering (only if same user)
        const cachedUserId = localStorage.getItem('cachedUserId');
        const cachedIsSuperAdmin = cachedUserId === session.user.id
          ? localStorage.getItem('isSuperAdmin') === 'true'
          : false;
        callback({
          id: session.user.id,
          email: session.user.email || '',
          fullName: session.user.user_metadata?.full_name || '',
          avatarUrl: session.user.user_metadata?.avatar_url || null,
          role: session.user.user_metadata?.role || 'sales_rep',
          isSuperAdmin: cachedIsSuperAdmin, // Use cached value to prevent flickering
          settings: {},
          createdAt: session.user.created_at || new Date().toISOString(),
          updatedAt: session.user.updated_at || new Date().toISOString(),
        });

        // Optionally fetch full profile in background (don't await - don't block)
        // This updates settings if available, but doesn't block login
        supabase
          .from('users')
          .select('*')
          .eq('id', session.user.id)
          .single()
          .then(({ data: userProfile, error: profileError }) => {
            if (!profileError && userProfile) {
              console.log('[AUTH] onAuthStateChange: updating with full profile');
              // Cache user ID and super admin status for next page load (prevents sidebar flickering)
              localStorage.setItem('cachedUserId', userProfile.id);
              localStorage.setItem('isSuperAdmin', String(userProfile.is_super_admin || false));
              callback({
                id: userProfile.id,
                email: session.user.email || '',
                fullName: userProfile.full_name,
                avatarUrl: userProfile.avatar_url,
                role: userProfile.role,
                isSuperAdmin: userProfile.is_super_admin || false,
                settings: userProfile.settings || {},
                createdAt: userProfile.created_at,
                updatedAt: userProfile.updated_at,
              });
            }
          });
      } else {
        console.log('[AUTH] onAuthStateChange: no session, calling callback with null');
        callback(null);
      }
    });
  }

  /**
   * Detect the registrable parent domain for the shared session cookie.
   *
   * Returns '.sellercockpit.com' on sellercockpit.com hosts,
   * '.feedback-os.com' on feedback-os.com hosts, and null for everything else
   * (localhost, 127.0.0.1, *.pages.dev Cloudflare preview deployments).
   * Null callers write the cookie without a domain attribute (current localhost behavior).
   */
  private sharedCookieDomain(hostname: string): string | null {
    const h = hostname.toLowerCase();
    if (h === 'sellercockpit.com' || h.endsWith('.sellercockpit.com')) return '.sellercockpit.com';
    if (h === 'feedback-os.com' || h.endsWith('.feedback-os.com')) return '.feedback-os.com';
    return null; // localhost / preview: no domain attribute
  }

  /**
   * Sync auth session to a shared cookie so the marketing sites can detect
   * logged-in users. Writes to '.sellercockpit.com' when running on sellercockpit.com
   * hosts and to '.feedback-os.com' when running on feedback-os.com hosts.
   * Cookie name 'sb-shared-session' stays constant (naturally namespaced by domain).
   * On localhost and Cloudflare preview deployments the cookie is written without
   * a domain attribute (same behavior as before this generalization).
   */
  private syncSessionCookie(event: string, session: { access_token: string; refresh_token: string } | null) {
    const COOKIE_NAME = 'sb-shared-session';
    const domain = this.sharedCookieDomain(window.location.hostname);
    const expired = 'Thu, 01 Jan 1970 00:00:00 GMT';

    if (event === 'SIGNED_OUT' || !session) {
      // Clear the domain-scoped cookie (covers production hosts)
      if (domain) {
        document.cookie = `${COOKIE_NAME}=; expires=${expired}; path=/; domain=${domain}; Secure; SameSite=Lax`;
      }
      // Always also clear the no-domain cookie so a stale localhost cookie cannot linger
      document.cookie = `${COOKIE_NAME}=; expires=${expired}; path=/;`;
      return;
    }

    if (session.access_token && session.refresh_token) {
      const value = JSON.stringify({
        access_token: session.access_token,
        refresh_token: session.refresh_token,
      });
      // 7-day expiry - Supabase refresh token handles actual auth validity
      const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
      if (domain) {
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires}; path=/; domain=${domain}; Secure; SameSite=Lax`;
      } else {
        // Localhost / preview: no domain restriction
        document.cookie = `${COOKIE_NAME}=${encodeURIComponent(value)}; expires=${expires}; path=/;`;
      }
    }
  }
}

export const authService = new AuthService();
